import { useState, useEffect } from "react"
import { useAuth } from "@/contexts/auth-context"
import { useParams, useNavigate } from "react-router-dom"
import { usersService, type User as UserWithRoles } from "@/services/users"
import { passwordService } from "@/services/password"
import { useRoleAccess } from "@/hooks/useRoleAccess"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { 
  AlertDialog, 
  AlertDialogAction, 
  AlertDialogCancel, 
  AlertDialogContent, 
  AlertDialogDescription, 
  AlertDialogFooter, 
  AlertDialogHeader, 
  AlertDialogTitle,
  AlertDialogTrigger 
} from "@/components/ui/alert-dialog"
import { AlertTriangle, Trash2, Eye, EyeOff } from "lucide-react"
import { toast } from "sonner"

export default function AccountPage() {
  const { user: authUser, logout } = useAuth()
  const { orgSlug } = useParams()
  const navigate = useNavigate()
  const { getPrimaryRole, hasAdminAccess, isOwner } = useRoleAccess()
  
  const [user, setUser] = useState<UserWithRoles | null>(null)
  const [formData, setFormData] = useState({
    name: authUser?.name || "",
    email: authUser?.email || ""
  })
  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: ""
  })
  
  const [loading, setLoading] = useState(false)
  const [passwordLoading, setPasswordLoading] = useState(false)
  const [removeLoading, setRemoveLoading] = useState(false)
  const [error, setError] = useState("")
  
  // Password visibility states
  const [showCurrentPassword, setShowCurrentPassword] = useState(false)
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  
  // Role display mapping
  const getRoleDisplayName = (role: string | null): string => {
    if (!role) return "No role"
    
    const roleMap: { [key: string]: string } = {
      viewer: "Viewer",
      editor: "Editor", 
      manager: "Manager",
      admin: "Admin",
      owner: "Owner"
    }
    
    return roleMap[role.toLowerCase()] || role
  }
  
  // Get user's current role in this organization
  const getCurrentRole = (): string => {
    // Check if user has admin access (system-wide admin or owner)
    if (hasAdminAccess()) {
      return isOwner() ? "Owner" : "Admin"
    }
    
    // Get primary role from organization context
    const primaryRole = getPrimaryRole()
    return getRoleDisplayName(primaryRole)
  }

  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        const userData = await usersService.getCurrentUser()
        setUser(userData)
        setFormData({
          name: userData.name || "",
          email: userData.email || ""
        })
      } catch (error) {
        console.error('Failed to fetch user data:', error)
      }
    }
    
    if (authUser) {
      fetchCurrentUser()
    }
  }, [authUser])

  useEffect(() => {
    if (authUser) {
      setFormData({
        name: authUser.name || "",
        email: authUser.email || ""
      })
    }
  }, [authUser])

  const handleProfileUpdate = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!user || !orgSlug) return
    
    setLoading(true)
    setError("")
    
    try {
      // Update user name (email updates not supported by current API)
      if (formData.name !== user.name) {
        await usersService.updateUser(orgSlug, user.id, { name: formData.name })
        toast.success("Profile updated successfully")
      }
      
      if (formData.email !== user.email) {
        const errorMsg = "Email updates are not currently supported. Please contact support."
        setError(errorMsg)
        toast.error(errorMsg)
      }
    } catch (err: any) {
      const errorMsg = err.message || "Failed to update profile"
      setError(errorMsg)
      toast.error(errorMsg)
    } finally {
      setLoading(false)
    }
  }

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault()
    setPasswordLoading(true)
    setError("")
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      const errorMsg = "New passwords do not match"
      setError(errorMsg)
      toast.error(errorMsg)
      setPasswordLoading(false)
      return
    }
    
    if (passwordData.newPassword.length < 8) {
      const errorMsg = "Password must be at least 8 characters long"
      setError(errorMsg)
      toast.error(errorMsg)
      setPasswordLoading(false)
      return
    }
    
    try {
      const response = await passwordService.changePassword({
        current_password: passwordData.currentPassword,
        new_password: passwordData.newPassword,
        new_password_confirmation: passwordData.confirmPassword
      })
      
      toast.success(response.message || "Password changed successfully")
      
      // Clear the form
      setPasswordData({
        currentPassword: "",
        newPassword: "",
        confirmPassword: ""
      })
    } catch (err: any) {
      const errorMsg = err.message || "Failed to change password"
      setError(errorMsg)
      toast.error(errorMsg)
    } finally {
      setPasswordLoading(false)
    }
  }

  const handleRemoveFromOrganization = async () => {
    if (!user || !orgSlug) return
    
    setRemoveLoading(true)
    setError("")
    
    try {
      if (isOwner()) {
        // Owner needs to delete organization - not implemented in current API
        setError("As the organization owner, you cannot remove yourself. Organization deletion is not currently available. Please contact support.")
      } else {
        // Remove user from organization
        await usersService.removeUser(orgSlug, user.id)
        toast.success("Successfully removed from organization")
        // Redirect to organizations page after removal
        setTimeout(() => {
          logout()
          navigate("/organizations")
        }, 2000)
      }
    } catch (err: any) {
      setError(err.message || "Failed to remove from organization")
    } finally {
      setRemoveLoading(false)
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Account</h1>
        <p className="text-muted-foreground">
          Manage your profile and account settings.
        </p>
      </div>

      {/* Alerts */}
      {error && (
        <div className="p-4 bg-red-50 dark:bg-red-950/50 border border-red-200 dark:border-red-800 rounded-lg">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-red-600 dark:text-red-400" />
            <p className="text-red-800 dark:text-red-200 text-sm">{error}</p>
          </div>
        </div>
      )}


      {/* Profile Information */}
      <Card>
        <CardHeader>
          <CardTitle>Profile Information</CardTitle>
          <CardDescription>
            Update your personal information and account details.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            {/* Account Info */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-muted/50 rounded-lg">
              <div className="text-sm">
                <span className="text-muted-foreground block">Role</span>
                <span className="font-medium">
                  {getCurrentRole()}
                </span>
              </div>
              <div className="text-sm">
                <span className="text-muted-foreground block">Member since</span>
                <span className="font-medium">
                  {user?.created_at ? new Date(user.created_at).toLocaleDateString() : "Unknown"}
                </span>
              </div>
              <div className="text-sm">
                <span className="text-muted-foreground block">Last sign in</span>
                <span className="font-medium">
                  {user?.last_sign_in_at ? new Date(user.last_sign_in_at).toLocaleDateString() : "Unknown"}
                </span>
              </div>
            </div>

            <form onSubmit={handleProfileUpdate} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="name">
                    Full Name
                  </Label>
                  <Input
                    id="name"
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">
                    Email Address
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    disabled
                    className="bg-muted"
                    title="Email changes are not currently supported"
                  />
                </div>
              </div>

              <div className="flex justify-end">
                <Button 
                  type="submit" 
                  disabled={loading}
                >
                  {loading ? "Saving..." : "Save Changes"}
                </Button>
              </div>
            </form>
          </div>
        </CardContent>
      </Card>

      {/* Security */}
      <Card>
        <CardHeader>
          <CardTitle>Security</CardTitle>
          <CardDescription>
            Manage your password and security settings.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handlePasswordChange} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="currentPassword">
                  Current Password
                </Label>
                <div className="relative">
                  <Input
                    id="currentPassword"
                    type={showCurrentPassword ? "text" : "password"}
                    value={passwordData.currentPassword}
                    onChange={(e) => setPasswordData(prev => ({ ...prev, currentPassword: e.target.value }))}
                    placeholder="Enter current password"
                    className="pr-10"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute top-1/2 right-2 h-6 w-6 -translate-y-1/2 p-0"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  >
                    {showCurrentPassword ? (
                      <EyeOff className="h-3.5 w-3.5" />
                    ) : (
                      <Eye className="h-3.5 w-3.5" />
                    )}
                    <span className="sr-only">Toggle current password visibility</span>
                  </Button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="newPassword">
                  New Password
                </Label>
                <div className="relative">
                  <Input
                    id="newPassword"
                    type={showNewPassword ? "text" : "password"}
                    value={passwordData.newPassword}
                    onChange={(e) => setPasswordData(prev => ({ ...prev, newPassword: e.target.value }))}
                    placeholder="Enter new password"
                    className="pr-10"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute top-1/2 right-2 h-6 w-6 -translate-y-1/2 p-0"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                  >
                    {showNewPassword ? (
                      <EyeOff className="h-3.5 w-3.5" />
                    ) : (
                      <Eye className="h-3.5 w-3.5" />
                    )}
                    <span className="sr-only">Toggle new password visibility</span>
                  </Button>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="p-4 bg-blue-50 dark:bg-blue-950/20 rounded-lg border border-blue-200 dark:border-blue-800">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="h-4 w-4 text-blue-600 dark:text-blue-400 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-blue-800 dark:text-blue-200">
                      Password Requirements
                    </p>
                    <p className="text-xs text-blue-700 dark:text-blue-300 mt-1">
                      Password must be 8-24 characters with at least one lowercase, uppercase, number, and special character (!@#$%).
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword">
                  Confirm New Password
                </Label>
                <div className="relative">
                  <Input
                    id="confirmPassword"
                    type={showConfirmPassword ? "text" : "password"}
                    value={passwordData.confirmPassword}
                    onChange={(e) => setPasswordData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                    placeholder="Confirm new password"
                    className="pr-10"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute top-1/2 right-2 h-6 w-6 -translate-y-1/2 p-0"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? (
                      <EyeOff className="h-3.5 w-3.5" />
                    ) : (
                      <Eye className="h-3.5 w-3.5" />
                    )}
                    <span className="sr-only">Toggle confirm password visibility</span>
                  </Button>
                </div>
              </div>
            </div>

            <div className="flex justify-end">
              <Button 
                type="submit" 
                disabled={passwordLoading || !passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword}
              >
                {passwordLoading ? "Changing..." : "Change Password"}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Danger Zone */}
      <Card className="border-red-200 dark:border-red-800">
        <CardHeader>
          <CardTitle className="text-red-600 dark:text-red-400">Danger Zone</CardTitle>
          <CardDescription>
            Irreversible and destructive actions
          </CardDescription>
        </CardHeader>
        <CardContent>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="destructive">
                <Trash2 className="h-4 w-4 mr-2" />
                {isOwner() ? "Delete Organization" : "Leave Organization"}
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>
                  {isOwner() ? "Delete Organization?" : "Leave Organization?"}
                </AlertDialogTitle>
                <AlertDialogDescription>
                  {isOwner() 
                    ? "This action cannot be undone. This will permanently delete the organization and remove all associated data."
                    : "This action cannot be undone. You will be removed from this organization and lose access immediately."
                  }
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>
                  Cancel
                </AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleRemoveFromOrganization}
                  disabled={removeLoading}
                  className="bg-red-600 focus:ring-red-600 disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  {removeLoading ? "Processing..." : (isOwner() ? "Delete Organization" : "Leave Organization")}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </CardContent>
      </Card>
    </div>
  )
}